/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useSwipeGesture.ts
 * PURPOSE: Custom React hooks for state management
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (React Best Practices)
 * SPRINT GOVERNANCE: Rule 9: Documentation Standards
 *
 *
 * @business-context
 * - Domain: React Hooks, State Management, Vietnamese F&B Business Logic
 * - Impact: Code Reusability, State Consistency, Developer Productivity
 * - Stakeholders: Development Team, Frontend Engineers
 *
 * @technical-architecture
 * - Layer: Business Logic Layer, Custom Hooks
 * - Pattern: React Hooks Pattern, State Management, API Integration
 * - Dependencies: React 18, TypeScript, API Services
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/FRONTEND-BACKEND-GAP-ANALYSIS-SPRINT10-13.md
 *  */

import { useRef, useCallback } from 'react';

export type SwipeDirection = 'left' | 'right' | 'up' | 'down';

interface SwipeConfig {
  /** Minimum distance in pixels to register as swipe */
  threshold?: number;
  /** Maximum time in ms for swipe gesture */
  maxTime?: number;
  /** Minimum velocity for swipe recognition */
  minVelocity?: number;
  /** Whether to prevent default touch behavior */
  preventDefault?: boolean;
}

interface TouchPoint {
  x: number;
  y: number;
  time: number;
}

interface SwipeCallbacks {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  onSwipe?: (direction: SwipeDirection) => void;
}

const DEFAULT_CONFIG: Required<SwipeConfig> = {
  threshold: 50,
  maxTime: 1000,
  minVelocity: 0.3,
  preventDefault: true
};

export const useSwipeGesture = (
  callbacks: SwipeCallbacks,
  config: SwipeConfig = {}
) => {
  const touchStart = useRef<TouchPoint | null>(null);
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    const touch = event.touches[0];
    touchStart.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };

    if (mergedConfig.preventDefault) {
      event.preventDefault();
    }
  }, [mergedConfig.preventDefault]);

  const handleTouchEnd = useCallback((event: React.TouchEvent) => {
    if (!touchStart.current) return;

    const touch = event.changedTouches[0];
    const touchEnd: TouchPoint = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };

    const deltaX = touchEnd.x - touchStart.current.x;
    const deltaY = touchEnd.y - touchStart.current.y;
    const deltaTime = touchEnd.time - touchStart.current.time;

    // Check if gesture is too slow
    if (deltaTime > mergedConfig.maxTime) {
      touchStart.current = null;
      return;
    }

    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const velocity = distance / deltaTime;

    // Check if gesture meets threshold and velocity requirements
    if (distance < mergedConfig.threshold || velocity < mergedConfig.minVelocity) {
      touchStart.current = null;
      return;
    }

    // Determine swipe direction
    let direction: SwipeDirection;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    if (absDeltaX > absDeltaY) {
      // Horizontal swipe
      direction = deltaX > 0 ? 'right' : 'left';
    } else {
      // Vertical swipe
      direction = deltaY > 0 ? 'down' : 'up';
    }

    // Trigger callbacks
    callbacks.onSwipe?.(direction);
    
    switch (direction) {
      case 'left':
        callbacks.onSwipeLeft?.();
        break;
      case 'right':
        callbacks.onSwipeRight?.();
        break;
      case 'up':
        callbacks.onSwipeUp?.();
        break;
      case 'down':
        callbacks.onSwipeDown?.();
        break;
    }

    // Haptic feedback if available
    if ('vibrate' in navigator && navigator.vibrate) {
      navigator.vibrate(50); // Short haptic feedback
    }

    touchStart.current = null;

    if (mergedConfig.preventDefault) {
      event.preventDefault();
    }
  }, [callbacks, mergedConfig]);

  const handleTouchMove = useCallback((event: React.TouchEvent) => {
    if (mergedConfig.preventDefault) {
      event.preventDefault();
    }
  }, [mergedConfig.preventDefault]);

  return {
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
    onTouchMove: handleTouchMove
  };
};